/**
 * The signed-in session — a single rune-backed store shared across the app.
 *
 * Auth reuses the customer portal accounts (email + password + optional TOTP);
 * the backend hands the PWA its own origin-scoped cookie (`kip_pwa`). Dropbox is
 * connected per account and reported here too so the shell can nudge for it.
 *
 * `GET /pwa/auth/session` is the source of truth; every mutation re-reads it.
 */
import { api, ApiError } from './api';

export interface SessionState {
  authenticated: boolean;
  mfaPending: boolean;
  email: string | null;
  dropboxConnected: boolean;
  dropboxAccountEmail: string | null;
  coopPath: string;
  /** Which OAuth providers the backend has configured (controls button visibility). */
  oauth: { google: boolean };
}

const EMPTY: SessionState = {
  authenticated: false,
  mfaPending: false,
  email: null,
  dropboxConnected: false,
  dropboxAccountEmail: null,
  coopPath: '',
  oauth: { google: false },
};

/** Where the browser goes to start Dropbox's PKCE flow (full-page navigation). */
export const DROPBOX_START_PATH = '/pwa/auth/dropbox/start';

/** Full-page navigation targets for the Google OAuth flow. */
export const GOOGLE_START_PATH = '/pwa/auth/oauth/google/start';
export const GOOGLE_LINK_PATH = '/pwa/auth/oauth/google/link';

class Session {
  state = $state<SessionState>({ ...EMPTY });
  /** false until the first `/pwa/auth/session` round-trip resolves. */
  loaded = $state(false);

  /** Authenticated and past any TOTP step — the app shell may render. */
  get ready(): boolean {
    return this.state.authenticated && !this.state.mfaPending;
  }

  async load(): Promise<void> {
    try {
      this.state = await api.get<SessionState>('/pwa/auth/session');
    } catch {
      this.state = { ...EMPTY };
    } finally {
      this.loaded = true;
    }
  }

  /** Returns true when a TOTP code is still required to finish signing in. */
  async login(email: string, password: string): Promise<boolean> {
    const r = await api.post<{ mfa_required: boolean }>('/pwa/auth/login', { email, password });
    await this.load();
    return Boolean(r.mfa_required);
  }

  /** Redeem an invite and start a session (invite-only signup). */
  async signup(email: string, password: string, inviteToken: string): Promise<void> {
    await api.post('/pwa/auth/signup', { email, password, invite_token: inviteToken });
    await this.load();
  }

  /** Resolve a pending invite token → { email, plan } (null when invalid). */
  async invite(token: string): Promise<{ email: string; plan: string } | null> {
    try {
      return await api.get<{ email: string; plan: string }>(
        `/pwa/auth/invite?token=${encodeURIComponent(token)}`,
      );
    } catch {
      return null;
    }
  }

  async submitTotp(code: string): Promise<void> {
    await api.post('/pwa/auth/login/totp', { code });
    await this.load();
  }

  async logout(): Promise<void> {
    try {
      await api.post('/pwa/auth/logout');
    } catch {
      /* best-effort — clear locally regardless */
    }
    this.state = { ...EMPTY };
  }

  async disconnectDropbox(): Promise<void> {
    await api.post('/pwa/auth/dropbox/disconnect');
    await this.load();
  }
}

export const session = new Session();
export { ApiError };
